/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { Icon } from '@az/starc-ui';
import { Car, Fit } from '@az/starc-ui-icons';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { usePageType } from '@/features/category/api/getPageType';
import styles from './styles.module.scss';
import { useLabels } from '@/hooks/useLabels';
import { Skeleton } from '@az/starc-ui';
import cx from 'classnames';

type VehicleFitmentMode = 'dealsForYour' | 'reviewDealThatFits';

type VehicleFitmentBadgeProps = {
  mode: VehicleFitmentMode;
  skuIsVehicleSpecific?: boolean;
  className?: string;
  as?: 'header' | 'span';
};

const LABEL_KEY_BY_MODE: Record<VehicleFitmentMode, string> = {
  dealsForYour: 'lbl_deals_for_your',
  reviewDealThatFits: 'lbl_review_deal_that_fits',
};

const ICON_BY_MODE: Record<VehicleFitmentMode, typeof Car | typeof Fit> = {
  dealsForYour: Car,
  reviewDealThatFits: Fit,
};

export const VehicleFitmentBadge = ({
  mode,
  skuIsVehicleSpecific,
  className,
  as: Component = 'header',
}: VehicleFitmentBadgeProps) => {
  const labels = useLabels({
    lblBanner: LABEL_KEY_BY_MODE[mode],
  });
  const { data: headerData, isLoading: isHeaderLoading } = useHeaderData();
  const { data: pageTypeData, isLoading: isPageTypeLoading } = usePageType();
  const preferredVehicle = getPreferredVehicle(headerData);

  const vehicle = preferredVehicle || pageTypeData;
  const selectedVehicle = vehicle
    ? [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')
    : '';
  const isLoading = isHeaderLoading || isPageTypeLoading;

  if (skuIsVehicleSpecific === false) {
    return null;
  }

  if (!selectedVehicle) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={cx(styles.fitment, className)}>
        <Skeleton width="24px" height="24px" />
        <Skeleton width="200px" height="16px" />
      </div>
    );
  }

  return (
    <Component
      className={cx(styles.fitment, className)}
      role={Component === 'header' ? 'banner' : undefined}
      aria-label={`${labels.lblBanner} ${selectedVehicle}`}
    >
      <Icon svg={ICON_BY_MODE[mode]} size={7.2} aria-hidden="true" />
      <span aria-live="polite">
        {labels.lblBanner} <strong>{selectedVehicle}</strong>
      </span>
    </Component>
  );
};
